import { Box } from '@mui/material';
import { CloudDownload, Plus, Share2, Trash } from 'lucide-react';
import useMediaStyles from '../../../../../../components/useMediaStyles';
import LangEnum from '../../../../../../lang/LangEnum';
import catalogModalStore from '../../../../../../stores/CatalogModal/CatalogModalStore';
import { CatalogTestIds } from '../../../../../../utils/testIds/catalog.testIds';
import { useApplicationContext } from '../../../../../context/ApplicationContext/ApplicationContext';
import { UsedInOfflineMode } from '../../../../../offline/types/UsedInOfflineMode';
import { Catalog } from '../../../../catalog.types';
import CatalogCardCheckbox from '../../../../components/CatalogCardCheckbox/CatalogCardCheckbox';
import { CATALOG_CREATION_MEDIA_ACCEPTED_TYPES } from '../../../../creation/catalogCreationMedia.consts';
import { CatalogActionsProps } from './components/CatalogActions';
import CatalogCardContent from './components/CatalogCardContent';
import { useCatalogCardCoverUpload } from './hooks/useCatalogCardCoverUpload';
import { useCatalogCardStatusToggle } from './hooks/useCatalogCardStatusToggle';
import { useCatalogCardStyles } from './styles/catalogCard.styles';

type ConfirmModalState = {
  open: boolean;
  item?: Catalog;
};

export type CatalogCardProps = UsedInOfflineMode & {
  catalog: Catalog;
  setItem: (catalog: Catalog) => void;
  setConfirmModal: ({ open, item }: ConfirmModalState) => void;
  read: boolean;
  isSelected?: boolean;
  onToggleSelect?: () => void;
  isAnySelected?: boolean;
  onCatalogUpdated: (catalog: Catalog) => void;
};

const CatalogCard = ({
  catalog,
  setItem,
  setConfirmModal,
  read,
  isSelected = false,
  onToggleSelect = () => {
    // default no-op
  },
  isAnySelected = false,
  onCatalogUpdated,
  isOfflineMode = false,
}: CatalogCardProps) => {
  const { classes: mediaClasses, cx } = useMediaStyles({});
  const { classes: catalogCardClasses } = useCatalogCardStyles();
  const { openModal } = catalogModalStore;
  const { isSeller } = useApplicationContext();

  const { fileInputRef, isUploadingCover, openFilePicker, handleSelectFile } =
    useCatalogCardCoverUpload({ catalog, onCatalogUpdated });

  const { statusActionLabel, statusActionIcon, toggleStatus } = useCatalogCardStatusToggle({
    catalogId: catalog.id,
    isCatalogActive: catalog.active,
    onCatalogUpdated,
  });

  const downloadPdfAction: CatalogActionsProps = {
    icon: CloudDownload,
    label: LangEnum.GLOBAL_EXPORT,
    onClick: () => {
      openModal({ catalog });
    },
  };

  const editCoverAction: CatalogActionsProps = {
    icon: Plus,
    label: LangEnum.CATALOGS_EDIT_COVER,
    onClick: openFilePicker,
  };

  const toggleStatusAction: CatalogActionsProps = {
    icon: statusActionIcon,
    label: statusActionLabel,
    onClick: toggleStatus,
  };

  const shareAction: CatalogActionsProps = {
    icon: Share2,
    label: LangEnum.GLOBAL_SHARE,
    onClick: () => setItem(catalog),
    isQuickAction: true,
  };

  const deleteAction: CatalogActionsProps = {
    icon: Trash,
    label: LangEnum.GLOBAL_DELETE,
    onClick: () => setConfirmModal({ open: true, item: catalog }),
  };

  const regularCatalogActions: CatalogActionsProps[] = [
    ...(!read ? [toggleStatusAction, editCoverAction] : []),
    shareAction,
    ...(!read || isSeller ? [downloadPdfAction] : []),
    ...(!read ? [deleteAction] : []),
  ];

  const defaultCatalogActions: CatalogActionsProps[] = !read
    ? [toggleStatusAction, editCoverAction]
    : [];

  const actions = catalog.isDefault ? defaultCatalogActions : regularCatalogActions;

  return (
    <Box
      className={cx(mediaClasses.root, catalogCardClasses.root)}
      sx={{ isolation: 'isolate' }}
      test-id={`${CatalogTestIds.CATALOG_CARD_PREFIX}${catalog.id}`}
    >
      {!read && (
        <CatalogCardCheckbox
          isSelected={isSelected}
          isAnySelected={isAnySelected}
          onToggleSelect={onToggleSelect}
        />
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept={CATALOG_CREATION_MEDIA_ACCEPTED_TYPES}
        hidden
        onChange={handleSelectFile}
      />

      <CatalogCardContent
        catalog={catalog}
        actions={isOfflineMode ? undefined : actions}
        isUploadingCover={isUploadingCover}
      />
    </Box>
  );
};

export default CatalogCard;
